import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../utils/pdfWorkerSetup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PdfViewer } from './PdfViewer';
import { OnlyOfficeEditor } from './OnlyOfficeEditor';
import { FieldToolbar } from './FieldToolbar';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { DataPlaceholderPanel } from './DataPlaceholderPanel';
import { useDsTranslate, useDsServices } from '../context/DsProvider';
import { usePinchZoom } from '../hooks/usePinchZoom';
import { RECIPIENT_COLORS } from '../utils/recipientColors';
import { getFieldDimensions, BASE_PDF_WIDTH } from '../utils/fieldDimensions';
import type { TemplateSigningField } from '../types/template';
import type { EnvelopeDocument } from '../types/envelope';
import styles from './TemplateBuilder.module.scss';

interface RecipientInfo {
  name: string;
  role: string;
  signingOrder?: number;
}

interface TemplateBuilderProps {
  templateId?: string;
  templateFormat: 'DOCX' | 'PDF';
  pdfUrl?: string;
  pageCount: number;
  sourceFileId?: string;
  documents?: EnvelopeDocument[];
  docPdfUrls?: Record<number, string>;
  activeDocumentOrder?: number;
  onActiveDocumentChange?: (order: number) => void;
  initialFields?: TemplateSigningField[];
  onFieldsChange?: (fields: TemplateSigningField[]) => void;
  onPageCountChange?: (count: number) => void;
  recipientCount?: number;
  recipients?: RecipientInfo[];
  readOnly?: boolean;
  onAutoScanStateChange?: (scanning: boolean) => void;
  /**
   * When true, the embedded FieldPropertiesPanel hides the "Prefilled by author"
   * option — used when the TemplateBuilder is hosted inside the envelope wizard
   * where author fields are inherited from the template and cannot be reassigned.
   */
  disableAuthorMode?: boolean;
}

export function TemplateBuilder({
  templateId, templateFormat, pdfUrl, pageCount, sourceFileId,
  documents, docPdfUrls, activeDocumentOrder, onActiveDocumentChange,
  initialFields = [], onFieldsChange, onPageCountChange,
  recipientCount = 10, recipients, readOnly = false, onAutoScanStateChange,
  disableAuthorMode = false,
}: TemplateBuilderProps) {
  const t = useDsTranslate();
  const queryClient = useQueryClient();
  const { template: templateService } = useDsServices();

  const { data: placeholdersList = [] } = useQuery({
    queryKey: ['ds-template-placeholders', templateId],
    queryFn: () => templateService!.getDataPlaceholders(templateId!),
    enabled: !!templateId && templateFormat === 'DOCX' && !!templateService,
  });
  const placeholderCount = placeholdersList.length;

  const [fields, setFields] = useState<TemplateSigningField[]>(initialFields);
  const [showAllSigners, setShowAllSigners] = useState(false);
  const overflowRef = useRef<HTMLSpanElement>(null);

  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [autoScanning, setAutoScanning] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  usePinchZoom(scrollAreaRef, { getZoom: () => zoomRef.current, onZoom: setZoom, max: 2 });

  // Cleanup scan timer on unmount
  useEffect(() => {
    return () => {
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    };
  }, []);

  const [actualPageCount, setActualPageCount] = useState(pageCount || 0);
  const [userPanel, setUserPanel] = useState<'fields' | 'placeholders'>(
    templateFormat === 'DOCX' ? 'placeholders' : 'fields',
  );
  const activePanel = selectedFieldIndex !== null ? 'properties' : userPanel;

  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | undefined>();
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [editorDocKey, setEditorDocKey] = useState<string | undefined>();

  useEffect(() => {
    if (templateFormat !== 'DOCX' || !templateId || pdfPreviewUrl || !templateService) return;
    if (activePanel !== 'fields' && activePanel !== 'properties') return;

    let cancelled = false;
    setPdfPreviewLoading(true);
    templateService.preview(templateId).then((res) => {
      if (!cancelled) setPdfPreviewUrl(URL.createObjectURL(res.data));
    }).catch(() => {}).finally(() => {
      if (!cancelled) setPdfPreviewLoading(false);
    });

    return () => { cancelled = true; };
  }, [templateFormat, templateId, activePanel, pdfPreviewUrl, templateService]);

  useEffect(() => {
    return () => { if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl); };
  }, [pdfPreviewUrl]);

  const isMultiDoc = documents && documents.length > 1;
  const currentDocOrder = activeDocumentOrder ?? 1;
  const activePdfUrl = docPdfUrls?.[currentDocOrder] ?? pdfUrl;
  const activeSourceFileId = sourceFileId;
  const currentDocPageCount = documents?.find((d) => d.documentOrder === currentDocOrder)?.pageCount ?? pageCount;
  const totalPages = actualPageCount || currentDocPageCount || 1;

  useEffect(() => {
    setCurrentPage(1);
    setSelectedFieldIndex(null);
    setActualPageCount(0);
  }, [currentDocOrder]);

  const selectedField = selectedFieldIndex !== null ? fields[selectedFieldIndex] ?? null : null;

  const handleFitToView = useCallback(() => {
    if (!scrollAreaRef.current) { setZoom(1); return; }
    const areaWidth = scrollAreaRef.current.clientWidth - 32;
    const fitZoom = areaWidth / BASE_PDF_WIDTH;
    setZoom(Math.max(0.3, Math.round(fitZoom * 100) / 100));
  }, []);

  const handleAddField = useCallback(
    (fieldType: string) => {
      const dims = getFieldDimensions(fieldType);
      let targetPage = currentPage;
      let xPosition = 100;
      let yPosition = 200;

      const scrollArea = scrollAreaRef.current;
      if (scrollArea) {
        const scrollRect = scrollArea.getBoundingClientRect();
        const viewportCenterY = scrollRect.top + scrollRect.height / 2;
        const pageEls = scrollArea.querySelectorAll('[data-page-number]');
        for (const pageEl of Array.from(pageEls)) {
          const rect = (pageEl as HTMLElement).getBoundingClientRect();
          if (viewportCenterY >= rect.top && viewportCenterY <= rect.bottom) {
            targetPage = parseInt((pageEl as HTMLElement).dataset.pageNumber || '1');
            xPosition = Math.max(0, Math.round((rect.width / 2) / zoom - dims.width / 2));
            yPosition = Math.max(0, Math.round((viewportCenterY - rect.top) / zoom - dims.height / 2));
            break;
          }
        }
      }

      const newField: TemplateSigningField = {
        fieldType: fieldType as TemplateSigningField['fieldType'],
        label: fieldType, recipientIndex: 1, pageNumber: targetPage,
        xPosition, yPosition, ...dims, isRequired: true, fontSize: 12,
        fieldOrder: fields.length, documentIndex: currentDocOrder,
      };
      const updated = [...fields, newField];
      setFields(updated);
      setSelectedFieldIndex(updated.length - 1);
      onFieldsChange?.(updated);
    },
    [fields, currentPage, currentDocOrder, zoom, onFieldsChange],
  );

  const handleDropField = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const fieldType = e.dataTransfer.getData('application/field-type');
      if (!fieldType || !scrollAreaRef.current) return;

      const scrollArea = scrollAreaRef.current;
      const dims = getFieldDimensions(fieldType);
      const pageEls = scrollArea.querySelectorAll('[data-page-number]');
      let targetPage = currentPage;
      let pageRect: DOMRect | null = null;

      for (const pageEl of Array.from(pageEls)) {
        const rect = (pageEl as HTMLElement).getBoundingClientRect();
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          targetPage = parseInt((pageEl as HTMLElement).dataset.pageNumber || '1');
          pageRect = rect;
          break;
        }
      }

      if (!pageRect) { handleAddField(fieldType); return; }

      const x = Math.max(0, Math.round((e.clientX - pageRect.left) / zoom - dims.width / 2));
      const y = Math.max(0, Math.round((e.clientY - pageRect.top) / zoom - dims.height / 2));

      const newField: TemplateSigningField = {
        fieldType: fieldType as TemplateSigningField['fieldType'],
        label: fieldType, recipientIndex: 1, pageNumber: targetPage,
        xPosition: x, yPosition: y, ...dims, isRequired: true, fontSize: 12,
        fieldOrder: fields.length, documentIndex: currentDocOrder,
      };
      const updated = [...fields, newField];
      setFields(updated);
      setSelectedFieldIndex(updated.length - 1);
      onFieldsChange?.(updated);
    },
    [fields, currentPage, currentDocOrder, zoom, onFieldsChange, handleAddField],
  );

  const handleFieldUpdate = useCallback(
    (index: number, updates: Partial<TemplateSigningField>) => {
      const updated = fields.map((f, i) => (i === index ? { ...f, ...updates } : f));
      setFields(updated);
      onFieldsChange?.(updated);
    },
    [fields, onFieldsChange],
  );

  const handleFieldDelete = useCallback(
    (index: number) => {
      const updated = fields.filter((_, i) => i !== index);
      setFields(updated);
      setSelectedFieldIndex(null);
      onFieldsChange?.(updated);
    },
    [fields, onFieldsChange],
  );

  const handleFieldMove = useCallback(
    (index: number, x: number, y: number) => { handleFieldUpdate(index, { xPosition: x, yPosition: y }); },
    [handleFieldUpdate],
  );

  const getFieldDocIndex = (f: TemplateSigningField) => f.documentIndex ?? 1;

  const currentDocFields = useMemo(
    () => fields.filter((f) => !isMultiDoc || getFieldDocIndex(f) === currentDocOrder),
    [fields, isMultiDoc, currentDocOrder],
  );

  return (
    <div className={styles.builder}>
      {isMultiDoc && documents && (
        <div className={styles.docSidebar}>
          <div className={styles.docSidebarHeader}>{t('K_DS_DOCUMENTS', 'Documents')}</div>
          <div className={styles.docSidebarList}>
            {documents.map((doc) => {
              const docFieldCount = fields.filter((f) => getFieldDocIndex(f) === doc.documentOrder).length;
              const isActive = doc.documentOrder === currentDocOrder;
              return (
                <button
                  key={doc.documentOrder}
                  className={`${styles.docThumb} ${isActive ? styles.docThumbActive : ''}`}
                  onClick={() => onActiveDocumentChange?.(doc.documentOrder)}
                  title={doc.name}
                >
                  <div className={styles.docThumbPreview}>
                    <span className={styles.docThumbOrderBadge}>{doc.documentOrder}</span>
                    {docPdfUrls?.[doc.documentOrder] ? (
                      <Document file={docPdfUrls[doc.documentOrder]} loading={null} error={null}>
                        <Page pageNumber={1} width={130} renderTextLayer={false} renderAnnotationLayer={false} />
                      </Document>
                    ) : null}
                  </div>
                  <div className={styles.docThumbInfo}>
                    <span className={styles.docThumbName}>{doc.name}</span>
                    <span className={styles.docThumbMeta}>
                      {doc.pageCount > 0 ? t('K_DS_PAGE_COUNT', '{count}pg').replace('{count}', String(doc.pageCount)) : ''}
                      {docFieldCount > 0 ? ` · ${docFieldCount} ${t('K_DS_FIELDS', 'fields')}` : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.viewerWrap}>
        <div className={styles.viewer}>
          {!(activePanel === 'placeholders' && templateFormat === 'DOCX' && activeSourceFileId) && (
            <div className={styles.pageNav}>
              <span>
                {t('K_DS_PAGE_OF', 'Page {page} of {total}')
                  .replace('{page}', String(currentPage))
                  .replace('{total}', String(totalPages))}
              </span>
              <span className={styles.zoomDivider}>|</span>
              <button onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(1)))}>−</button>
              <span>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(1)))}>+</button>
              <button className={styles.fitBtn} onClick={handleFitToView}>
                {t('K_DS_FIT_WIDTH', 'Fit Width')}
              </button>
            </div>
          )}

          <div
            className={styles.scrollArea}
            ref={scrollAreaRef}
            onDragOver={activePanel === 'placeholders' && templateFormat === 'DOCX' ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
            onDrop={activePanel === 'placeholders' && templateFormat === 'DOCX' ? undefined : handleDropField}
          >
            {activePanel === 'placeholders' && templateFormat === 'DOCX' && activeSourceFileId ? (
              <OnlyOfficeEditor
                fileId={activeSourceFileId}
                mode="edit"
                onDocKey={setEditorDocKey}
                onSave={() => {
                  if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
                  setAutoScanning(true);
                  onAutoScanStateChange?.(true);

                  scanTimerRef.current = setTimeout(() => {
                    (async () => {
                      try {
                        if (editorDocKey && templateService) {
                          try { await templateService.forceSave(editorDocKey); } catch { /* ignore */ }
                        }
                        if (templateId && templateService) {
                          try { await templateService.scanPlaceholders(templateId); } catch { /* ignore */ }
                          queryClient.invalidateQueries({ queryKey: ['ds-template-placeholders', templateId] });
                        }
                        if (pdfPreviewUrl) { URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(undefined); }
                      } finally {
                        setAutoScanning(false);
                        onAutoScanStateChange?.(false);
                      }
                    })();
                  }, 3000);
                }}
              />
            ) : pdfPreviewLoading && !activePdfUrl ? (
              <div className={styles.pdfLoader}>
                <span className={styles.pdfLoaderSpinner} />
                {t('K_DS_GENERATING_PREVIEW', 'Generating PDF preview...')}
              </div>
            ) : (
              <PdfViewer
                url={templateFormat === 'DOCX' ? (pdfPreviewUrl ?? activePdfUrl) : activePdfUrl}
                pageNumber={currentPage}
                fields={currentDocFields}
                allFields={fields}
                selectedFieldIndex={readOnly ? null : selectedFieldIndex}
                onFieldSelect={readOnly ? () => {} : setSelectedFieldIndex}
                onFieldMove={readOnly ? () => {} : handleFieldMove}
                zoom={zoom}
                onFieldPageChange={(index, page) => handleFieldUpdate(index, { pageNumber: page })}
                onDocumentLoad={(n) => { setActualPageCount(n); onPageCountChange?.(n); }}
                onVisiblePageChange={setCurrentPage}
              />
            )}
          </div>
        </div>

        <div className={styles.fieldCount}>
          <span>
            {currentDocFields.filter((f) => f.pageNumber === currentPage).length}{' '}
            {t('K_DS_FIELDS_ON_PAGE', 'field(s) on page')} {currentPage}
            {' · '}{fields.length} {t('K_DS_TOTAL', 'total')}
          </span>
          {recipients && recipients.length > 0 && (() => {
            const maxVisible = 5;
            const visible = recipients.slice(0, maxVisible);
            const overflowCount = recipients.length - maxVisible;
            return (
              <span className={styles.signerLegend}>
                {visible.map((r, i) => (
                  <span key={i} className={styles.signerLegendItem} title={r.name || `${t('K_DS_SIGNER', 'Signer')} ${i + 1}`}>
                    <span className={styles.signerLegendDot} style={{ background: RECIPIENT_COLORS[i % RECIPIENT_COLORS.length] }} />
                    <span className={styles.signerLegendName}>{r.name || `${t('K_DS_SIGNER', 'Signer')} ${i + 1}`}</span>
                  </span>
                ))}
                {overflowCount > 0 && (
                  <span ref={overflowRef} className={styles.signerLegendOverflowWrap} onMouseEnter={() => setShowAllSigners(true)} onMouseLeave={() => setShowAllSigners(false)}>
                    <span className={styles.signerLegendOverflow}>+{overflowCount} {t('K_DS_MORE', 'more')}</span>
                    {showAllSigners && overflowRef.current && createPortal(
                      (() => {
                        const rect = overflowRef.current!.getBoundingClientRect();
                        return (
                          <div className={styles.signerLegendPopover} style={{ position: 'fixed', top: rect.top - 8, right: window.innerWidth - rect.right, transform: 'translateY(-100%)' }} onMouseEnter={() => setShowAllSigners(true)} onMouseLeave={() => setShowAllSigners(false)}>
                            <div className={styles.signerLegendPopoverHeader}>
                              {t('K_DS_ALL_SIGNERS', 'All signers ({count})').replace('{count}', String(recipients.length))}
                            </div>
                            {recipients.map((r, i) => (
                              <div key={i} className={styles.signerLegendPopoverItem}>
                                <span className={styles.signerLegendDot} style={{ background: RECIPIENT_COLORS[i % RECIPIENT_COLORS.length] }} />
                                {r.name || `${t('K_DS_SIGNER', 'Signer')} ${i + 1}`}
                              </div>
                            ))}
                          </div>
                        );
                      })(),
                      document.body,
                    )}
                  </span>
                )}
              </span>
            );
          })()}
        </div>
      </div>

      {!readOnly && (
        <div className={styles.rightPanel}>
          <div className={styles.panelTabs}>
            <button className={`${styles.panelTab} ${activePanel === 'fields' ? styles.active : ''}`} onClick={() => { setUserPanel('fields'); setSelectedFieldIndex(null); }}>
              {t('K_DS_ADD_FIELDS', 'Add Fields')}
            </button>
            {templateFormat === 'DOCX' && (
              <button className={`${styles.panelTab} ${activePanel === 'placeholders' ? styles.active : ''}`} onClick={() => { setUserPanel('placeholders'); setSelectedFieldIndex(null); }}>
                {t('K_DS_PLACEHOLDERS', 'Placeholders')}{placeholderCount > 0 ? ` (${placeholderCount})` : ''}
              </button>
            )}
            {selectedField && (
              <button className={`${styles.panelTab} ${activePanel === 'properties' ? styles.active : ''}`}>
                {t('K_DS_PROPERTIES', 'Properties')}
              </button>
            )}
          </div>
          <div className={styles.rightPanelContent}>
            {activePanel === 'properties' && selectedField ? (
              <FieldPropertiesPanel field={selectedField} fieldIndex={selectedFieldIndex!} recipientCount={recipientCount} recipients={recipients} onUpdate={handleFieldUpdate} onDelete={handleFieldDelete} disableAuthorMode={disableAuthorMode} />
            ) : activePanel === 'placeholders' && templateFormat === 'DOCX' ? (
              <DataPlaceholderPanel templateId={templateId} docKey={editorDocKey} autoScanning={autoScanning} />
            ) : (
              <FieldToolbar onAddField={handleAddField} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
